import {
  normaliseName,
  wineTypeSchema,
  type MyAreaWish,
  type PublishedAreaReport,
  type WineType,
} from "@corklog/shared";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import {
  ApiError,
  fetchMyAreaWishes,
  fetchPublishedAreaReports,
  wishAreaReport,
} from "../api.js";
import {
  actionButton,
  control,
  fieldLabel,
  resting,
  Select,
} from "../components/controls.js";
import { CellarHeading } from "../components/CellarHeading.js";
import { CountrySelect } from "../components/CountrySelect.js";
import { Flag } from "../components/Flag.js";
import { useCellarCountries } from "../cellar-countries.js";
import { countryName, orderCountries } from "../countries.js";
import { locale } from "../locale.js";
import { useReload } from "../offline.js";

type State =
  | { status: "loading" }
  | { status: "ready"; reports: PublishedAreaReport[] }
  | { status: "switchedOff" }
  | { status: "failed" };

const KINDS = wineTypeSchema.options;

/**
 * *Upptäck* — the published area reports, as a tree: kind, then country, then
 * area. Task 10.34; decided 2026-09-28 in `project-scope.md`.
 *
 * **Kind first**, because it is what a reader starts from when choosing what
 * to read about, and the axis the rest of the application already sorts by.
 * The same area can therefore stand under two kinds — Loire under *Vitt* and
 * under *Crémant* — which is right: they are two reports.
 *
 * **Only what is published is drawn**, so a kind or a country with nothing in
 * it does not appear at all. A country is a grouping and never a report.
 *
 * **Not in any cellar**, since a report belongs to none. It was reached from
 * the corner, beside the name, until 2026-10-07; it is reached from *Mer*
 * since, and wears the heading of the cellar last opened so the way on from
 * here is the same as from anywhere.
 *
 * The tree is `<details>`: folding is the browser's own, keyboard and screen
 * reader included, and every kind starts open when there are few enough that
 * the whole tree fits on a phone without folding anything.
 */
export function Discover() {
  const { t } = useTranslation();
  const [state, setState] = useState<State>({ status: "loading" });

  const load = useCallback(() => {
    void fetchPublishedAreaReports()
      .then(({ reports }) => setState({ status: "ready", reports }))
      .catch((error: unknown) =>
        setState({
          status:
            error instanceof ApiError && error.code === "switched_off"
              ? "switchedOff"
              : "failed",
        }),
      );
  }, []);

  useReload(load);

  const reports = state.status === "ready" ? state.reports : [];
  const kinds = KINDS.filter((kind) => reports.some((report) => report.kind === kind));
  const openAll = reports.length <= 12;

  return (
    <section className="space-y-8">
      <div>
        {/* The heading every cellar's page wears, since 2026-10-08: the row
            and *Mer* of the cellar last opened, so the way on from here is the
            way on from everywhere else. The administrator's link to
            *Rapporter och önskemål* that stood under the intro went into *Mer*
            the same day. */}
        <CellarHeading cellarId={null} current="discover" />
        <p className="text-muted mt-2 max-w-prose text-sm">{t("discover.intro")}</p>
      </div>

      {state.status === "loading" && <p className="text-muted text-sm">{t("loading")}</p>}
      {state.status === "switchedOff" && (
        // Quiet, as `SwitchedOff` is for the questions: nothing has gone
        // wrong, the administrator has not opened it to this account.
        <p className="text-muted max-w-prose text-sm">{t("discover.switchedOff")}</p>
      )}
      {state.status === "failed" && (
        <p role="alert" className="text-past text-sm">
          {t("discover.loadFailed")}
        </p>
      )}
      {state.status === "ready" && reports.length === 0 && (
        <p className="text-muted max-w-prose text-sm">{t("discover.empty")}</p>
      )}

      {kinds.length > 0 && (
        <div className="border-line bg-surface divide-line max-w-prose divide-y rounded-sm border">
          {kinds.map((kind) => (
            <KindNode
              key={kind}
              kind={kind}
              reports={reports.filter((report) => report.kind === kind)}
              open={openAll}
            />
          ))}
        </div>
      )}

      {state.status === "ready" && <Wishes published={reports} />}
    </section>
  );
}

type WishesState =
  | { status: "loading" }
  | { status: "ready"; wishes: MyAreaWish[] }
  | { status: "failed" };

/**
 * Wishing for a report, and the reader's own wishes. Task 10.33.
 *
 * **Anybody may wish**, guests included, and nobody but the administrator
 * sees who else did: a reader is told *du och två till*, never the names.
 * **A report already in the tree is not wished for** — the form links to it
 * instead, which is what the wisher wanted.
 */
function Wishes({ published }: { published: readonly PublishedAreaReport[] }) {
  const { t } = useTranslation();
  const [state, setState] = useState<WishesState>({ status: "loading" });
  const [kind, setKind] = useState<WineType>("Red");
  const [countryCode, setCountryCode] = useState("FR");
  const [area, setArea] = useState("");
  const [why, setWhy] = useState("");
  const countryChoices = orderCountries(useCellarCountries());
  const [answer, setAnswer] = useState<
    | { tone: "done" | "failed"; text: string }
    | { tone: "exists"; report: PublishedAreaReport }
    | null
  >(null);

  const load = useCallback(() => {
    void fetchMyAreaWishes()
      .then(({ wishes }) => setState({ status: "ready", wishes }))
      .catch(() => setState({ status: "failed" }));
  }, []);

  useReload(load);

  const wish = () => {
    const trimmed = area.trim();
    if (trimmed === "") {
      setAnswer({ tone: "failed", text: t("discover.wish.areaMissing") });
      return;
    }
    setAnswer(null);
    void wishAreaReport({ kind, countryCode, area: trimmed, why })
      .then(() => {
        setAnswer({ tone: "done", text: t("discover.wish.sent") });
        setArea("");
        setWhy("");
        load();
      })
      .catch((error: unknown) => {
        const existing = published.find(
          (report) =>
            report.kind === kind &&
            report.countryCode === countryCode &&
            normaliseName(report.area) === normaliseName(trimmed),
        );
        if (error instanceof ApiError && error.code === "already_published" && existing) {
          setAnswer({ tone: "exists", report: existing });
        } else {
          setAnswer({ tone: "failed", text: t("discover.wish.failed") });
        }
      });
  };

  return (
    <>
      <section className="max-w-prose space-y-3">
        <h2 className="font-display text-xl">{t("discover.wish.title")}</h2>
        <p className="text-muted text-sm">{t("discover.wish.intro")}</p>
        <form
          className="grid gap-3 sm:grid-cols-3"
          onSubmit={(event) => {
            event.preventDefault();
            wish();
          }}
        >
          <div>
            <label className={fieldLabel} htmlFor="wish-kind">
              {t("areaReports.kind")}
            </label>
            <div className="mt-1">
              <Select
                id="wish-kind"
                value={kind}
                onChange={(next) => setKind(next as WineType)}
              >
                {KINDS.map((option) => (
                  <option key={option} value={option}>
                    {t(`wineType.${option}`)}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          <div>
            <label className={fieldLabel} htmlFor="wish-country">
              {t("areaReports.country")}
            </label>
            <div className="mt-1">
              <CountrySelect
                id="wish-country"
                value={countryCode}
                onChange={(next) => setCountryCode(next ?? countryCode)}
                {...countryChoices}
              />
            </div>
          </div>
          <div>
            <label className={fieldLabel} htmlFor="wish-area">
              {t("areaReports.area")}
            </label>
            <input
              id="wish-area"
              value={area}
              onChange={(event) => setArea(event.target.value)}
              autoComplete="off"
              maxLength={80}
              placeholder={t("discover.wish.areaPlaceholder")}
              className={`${control} ${resting} mt-1`}
            />
          </div>
          <div className="sm:col-span-3">
            <label className={fieldLabel} htmlFor="wish-why">
              {t("discover.wish.why")}
            </label>
            <input
              id="wish-why"
              value={why}
              onChange={(event) => setWhy(event.target.value)}
              autoComplete="off"
              maxLength={200}
              placeholder={t("discover.wish.whyPlaceholder")}
              className={`${control} ${resting} mt-1`}
            />
          </div>
          <div>
            <button type="submit" className={actionButton}>
              {t("discover.wish.send")}
            </button>
          </div>
        </form>
        {answer !== null && answer.tone === "exists" && (
          <p role="status" className="text-sm">
            <Link
              to={`/upptack/${encodeURIComponent(answer.report.id)}`}
              className="text-accent underline-offset-4 hover:underline"
            >
              {t("discover.wish.exists", { area: answer.report.area })}
            </Link>
          </p>
        )}
        {answer !== null && answer.tone !== "exists" && (
          <p
            role={answer.tone === "failed" ? "alert" : "status"}
            className={`text-sm ${answer.tone === "failed" ? "text-past" : "text-ready"}`}
          >
            {answer.text}
          </p>
        )}
      </section>

      <section className="max-w-prose space-y-3">
        <h2 className="font-display text-xl">{t("discover.wish.mine")}</h2>
        {state.status === "failed" && (
          <p role="alert" className="text-past text-sm">
            {t("discover.wish.loadFailed")}
          </p>
        )}
        {state.status === "ready" && state.wishes.length === 0 && (
          <p className="text-muted text-sm">{t("discover.wish.none")}</p>
        )}
        {state.status === "ready" && state.wishes.length > 0 && (
          <ul className="border-line bg-surface divide-line divide-y rounded-sm border">
            {state.wishes.map((wish) => (
              <li
                key={wish.id}
                className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-4 py-3"
              >
                <span className="font-semibold">
                  {t(`wineType.${wish.kind}`)} · {countryName(wish.countryCode)} ·{" "}
                  {wish.area}
                </span>
                {wish.state === "met" && wish.reportId !== null ? (
                  <Link
                    to={`/upptack/${encodeURIComponent(wish.reportId)}`}
                    className="text-ready text-sm underline-offset-4 hover:underline"
                  >
                    {t("discover.wish.state.met")}
                  </Link>
                ) : (
                  <span
                    className={`text-sm ${wish.state === "declined" ? "text-past" : wish.state === "met" ? "text-ready" : "text-muted"}`}
                  >
                    {t(`discover.wish.state.${wish.state}`)}
                  </span>
                )}
                <span className="text-muted text-xs">
                  {wish.state === "open" && wish.others > 0
                    ? t("discover.wish.wishedWithOthers", {
                        date: formatDate(wish.wishedAt),
                        count: wish.others,
                      })
                    : t("discover.wish.wishedOn", { date: formatDate(wish.wishedAt) })}
                </span>
                {wish.state === "declined" && wish.reason !== null && (
                  <span className="text-muted w-full text-sm">”{wish.reason}”</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(locale(), { day: "numeric", month: "short" });
}

function KindNode({
  kind,
  reports,
  open,
}: {
  kind: WineType;
  reports: readonly PublishedAreaReport[];
  open: boolean;
}) {
  const { t } = useTranslation();
  const countries = [...new Set(reports.map((report) => report.countryCode))].sort(
    (a, b) => countryName(a).localeCompare(countryName(b), locale()),
  );

  return (
    <details open={open} className="group/kind">
      <summary className="font-display flex cursor-pointer list-none items-baseline gap-3 px-4 py-3 text-lg [&::-webkit-details-marker]:hidden">
        <Caret />
        {t(`wineType.${kind}`)}
        <span className="text-muted font-sans text-sm tabular-nums">
          {t("discover.areaCount", { count: reports.length })}
        </span>
      </summary>
      <div className="pb-3">
        {countries.map((code) => {
          const areas = reports
            .filter((report) => report.countryCode === code)
            .sort((a, b) => a.area.localeCompare(b.area, "sv"));
          return (
            <details key={code} open className="ml-6">
              <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-1.5 [&::-webkit-details-marker]:hidden">
                <Caret />
                <Flag countryCode={code} />
                <span>{countryName(code)}</span>
                <span className="text-muted text-sm tabular-nums">{areas.length}</span>
              </summary>
              <ul className="mb-2 ml-14 space-y-1">
                {areas.map((report) => (
                  <li key={report.id}>
                    <Link
                      to={`/upptack/${encodeURIComponent(report.id)}`}
                      className="text-accent font-semibold underline-offset-4 hover:underline"
                    >
                      {report.area}
                    </Link>
                  </li>
                ))}
              </ul>
            </details>
          );
        })}
      </div>
    </details>
  );
}

/** The fold's mark, turned by the open state of the nearest `<details>`. */
function Caret() {
  return (
    <svg
      viewBox="0 0 6 10"
      aria-hidden="true"
      className="text-muted h-2.5 w-1.5 shrink-0 transition-transform motion-reduce:transition-none [details[open]>summary>&]:rotate-90"
    >
      <path
        d="M1 1l4 4-4 4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
